import { usePModal } from '@pequity/squirrel';
import { markRaw, ref } from 'vue';
import { confirmationModalName } from '@/components/confirmation-modal/constants';
import { type ConfirmationModalOptions } from '@/components/confirmation-modal/types';
import { isComponent } from '@/utils/component';

const defaults = (): Required<ConfirmationModalOptions> => ({
  type: 'default',
  title: 'Confirmation',
  body: 'Are you sure you want to proceed?',
  confirmButtonText: 'Confirm',
  cancelButtonText: 'Cancel',
  onConfirm: () => {},
  onCancel: () => {},
  onBeforeOpen: () => {},
  onClosed: () => {},
});

const options = ref<ConfirmationModalOptions>(defaults());

const confirmationModalErrorMsg = ref('');

export const useConfirmationModal = () => {
  const isLoading = ref(false);
  const modal = usePModal();

  const onBeforeOpen = () => {
    options.value.onBeforeOpen?.();
  };

  const onClosed = () => {
    options.value.onClosed?.();
    options.value = defaults();
    isLoading.value = false;
    confirmationModalErrorMsg.value = '';
  };

  const showConfirmationModal = (modalOptions?: ConfirmationModalOptions) => {
    // Mark body as raw to prevent Vue from converting it to a reactive object
    if (isComponent(modalOptions?.body)) {
      modalOptions.body = markRaw(modalOptions.body);
    }

    options.value = { ...defaults(), ...modalOptions };
    modal.show(confirmationModalName);
  };

  const hideConfirmationModal = () => {
    modal.hide(confirmationModalName);
  };

  const onConfirm = async () => {
    const onConfirm = options.value?.onConfirm;

    // Set isLoading only for async functions
    if (onConfirm && onConfirm.constructor.name === 'AsyncFunction') {
      isLoading.value = true;
    }

    await onConfirm?.();

    // Show error message if any and stop loading
    if (confirmationModalErrorMsg.value) {
      isLoading.value = false;
      return;
    }

    isLoading.value = false;
    hideConfirmationModal();
  };

  const onCancel = async () => {
    await options.value?.onCancel?.();

    hideConfirmationModal();
  };

  return {
    confirmationModalOptions: options,
    confirmationModalErrorMsg,
    isLoading,
    showConfirmationModal,
    hideConfirmationModal,
    onConfirm,
    onCancel,
    onBeforeOpen,
    onClosed,
  };
};
